import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GALLERY, BEFORE_AFTER, CATEGORIES } from "@/data/site";
import SEO from "@/components/SEO";
import { Reveal, MaskedLines } from "@/components/Reveal";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { BookButton } from "@/components/CTAButtons";
import { CTABanner } from "@/components/sections";

const FILTERS = [{ slug: "all", short: "All" }, ...CATEGORIES];

export default function Gallery() {
  const [filter, setFilter] = useState("all");
  const items = filter === "all" ? GALLERY : GALLERY.filter((g) => g.cat === filter);
  const ba = filter === "all" ? BEFORE_AFTER : BEFORE_AFTER.filter((b) => b.cat === filter);

  return (
    <>
      <SEO
        title="Gallery & Lookbook — Before & After Transformations, Hell's Kitchen NYC"
        description="Browse Maison Neuf's lookbook of hair color, nail art, facial glow-ups and brow transformations. Filter by service and book the look — Hell's Kitchen, Manhattan."
      />
      <section className="mx-auto max-w-[1400px] px-5 pt-36 pb-12 md:px-10 md:pt-44" data-testid="gallery-hero">
        <p className="label mb-6 text-wine">Gallery / Lookbook</p>
        <h1 className="max-w-4xl font-display text-5xl leading-[1.0] md:text-8xl">
          <MaskedLines lines={["The proof", "is in the mirror."]} />
        </h1>
      </section>

      {/* FILTERS */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10" data-testid="gallery-filters">
        <div className="flex flex-wrap gap-3 border-y border-border py-6">
          {FILTERS.map((f) => (
            <button
              key={f.slug}
              data-testid={`gallery-filter-${f.slug}`}
              onClick={() => setFilter(f.slug)}
              className={`px-5 py-2.5 font-mono text-xs uppercase tracking-[0.16em] transition-colors ${
                filter === f.slug ? "bg-foreground text-background" : "border border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {f.short}
            </button>
          ))}
        </div>
      </section>

      {/* BEFORE / AFTER */}
      {ba.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-5 pt-16 md:px-10" data-testid="gallery-before-after">
          <p className="label mb-8 text-wine">Drag to compare</p>
          <div className="grid gap-8 md:grid-cols-2">
            {ba.map((b, i) => (
              <Reveal key={b.id} delay={i * 0.08}>
                <BeforeAfterSlider before={b.before} after={b.after} title={b.title} alt={b.title} />
                <div className="mt-4">
                  <BookButton label="Book this look" category={b.cat} solid={false} testid={`gallery-book-ba-${b.id}`} className="!py-3" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* BENTO GRID */}
      <section className="mx-auto max-w-[1400px] px-5 py-16 md:px-10 md:py-24" data-testid="gallery-grid">
        <p className="label mb-8">The lookbook</p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {items.map((g, i) => (
              <motion.div
                key={g.id}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, delay: (i % 8) * 0.03 }}
                className={`group relative overflow-hidden bg-secondary ${i % 5 === 0 ? "row-span-2 aspect-[3/4]" : "aspect-square"}`}
                data-testid={`gallery-item-${g.id}`}
              >
                <div className="clip-frame absolute inset-0">
                  <img src={g.img} alt={`${g.title} — ${g.cat} transformation at Maison Neuf, Hell's Kitchen NYC`} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-background/90 via-background/10 to-transparent p-5 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  <p className="font-display text-lg leading-tight">{g.title}</p>
                  <div className="mt-3">
                    <BookButton label="Book this look" category={g.cat} solid={false} testid={`gallery-book-${g.id}`} className="!px-4 !py-2 !text-[0.65rem]" />
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
